/**
 * Kanban Board Component
 * Main board with 4 columns: Backlog, Doing, Review, Done
 */
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCorners } from '@dnd-kit/core'
import { useState } from 'react'
import type { ActionItemsGrouped, ActionStatus, ActionItem } from '@/types/actions'
import { BoardColumn } from './BoardColumn'
import { ActionItemCard } from './ActionItemCard'
import { useMoveActionItem } from '@/hooks/useActions'
import { useToast } from '@/hooks/use-toast'

interface KanbanBoardProps {
  actionItems: ActionItemsGrouped
  onCardClick: (actionId: string) => void
  onCreateClick: (status: string) => void
}

const COLUMNS: { key: ActionStatus; title: string }[] = [
  { key: 'backlog', title: 'Backlog' },
  { key: 'doing', title: 'Doing' },
  { key: 'review', title: 'Review' },
  { key: 'done', title: 'Done' },
]

export function KanbanBoard({ actionItems, onCardClick, onCreateClick }: KanbanBoardProps) {
  const [activeItem, setActiveItem] = useState<ActionItem | null>(null)
  const moveMutation = useMoveActionItem()
  const { toast } = useToast()

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    const draggedItem = findItemById(active.id as string)
    setActiveItem(draggedItem)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveItem(null)

    if (!over) return

    const actionId = active.id as string
    const newStatus = over.id as ActionStatus
    const currentItem = findItemById(actionId)

    // Don't move if dropped in same column
    if (currentItem?.status === newStatus) return

    try {
      await moveMutation.mutateAsync({ actionId, status: newStatus })
      toast({
        title: 'Item moved',
        description: `Moved to ${COLUMNS.find(c => c.key === newStatus)?.title}`,
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to move item. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const findItemById = (id: string): ActionItem | null => {
    for (const column of COLUMNS) {
      const item = actionItems[column.key]?.find((item) => item.id === id)
      if (item) return item
    }
    return null
  }

  return (
    <DndContext
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-full p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 h-full">
          {COLUMNS.map((column) => (
            <BoardColumn
              key={column.key}
              title={column.title}
              status={column.key}
              items={actionItems[column.key] || []}
              onCardClick={onCardClick}
              onCreateClick={() => onCreateClick(column.key)}
            />
          ))}
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeItem ? (
          <div className="rotate-3 opacity-90">
            <ActionItemCard item={activeItem} onClick={() => {}} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
